import { IndicatorSeries, PolygonType, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';

export type ForeignHoldingDivergence = 'confirm-bull' | 'distribution' | 'accumulation' | 'confirm-bear';

export interface ForeignHoldingChartPoint {
  /** Unix seconds of the report date */
  time: number;
  date: string;
  /** foreign_holding / qty_cds * 100, null if unavailable or the source guard rejected it */
  pct: number | null;
  /** Raw shares held by foreign investors */
  foreignHolding: number;
  /** foreignHolding[t] - foreignHolding[t-1], null for the first point or right after a gap */
  delta: number | null;
  /** delta as a % of foreignHolding[t-1], null for the first point, a gap, or a zero prior value */
  deltaPct: number | null;
  /** Written by the indicator's calc() as a side effect (not the fetch hook)
   *  so ChartLegend's crosshair block can show the same divergence read the
   *  chart itself is drawing, without duplicating the comparison logic. */
  divergence?: ForeignHoldingDivergence | null;
}

export function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

export function fmtShares(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

// Gap wider than this (calendar days) means the underlying report wasn't
// published for one or more days — a rolling average or divergence read
// spanning that gap wouldn't be a meaningful day-to-day comparison, so the
// MA window is reset and divergence is skipped across it.
const GAP_DAYS_THRESHOLD = 4;

type KBar = { timestamp: number; close: number };

// Same nearest-bar-to-timestamp binary search as epsLine.ts — duplicated
// rather than imported since these two indicators shouldn't be coupled.
function findClosestBarIndex(dataList: KBar[], targetUnixSec: number): number {
  if (dataList.length === 0) return -1;
  let lo = 0;
  let hi = dataList.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const midSec = Math.floor(dataList[mid].timestamp / 1000);
    if (midSec < targetUnixSec) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0) {
    const loSec = Math.floor(dataList[lo].timestamp / 1000);
    const prevSec = Math.floor(dataList[lo - 1].timestamp / 1000);
    if (Math.abs(prevSec - targetUnixSec) <= Math.abs(loSec - targetUnixSec)) return lo - 1;
  }
  return lo;
}

interface CalcResult {
  /** Absolute shares held by foreign investors on this report date, null on
   *  bars with no matching snapshot (klinecharts simply skips drawing those). */
  absHolding: number | null;
  /** MA1/MA2-period rolling average of absHolding over the last N snapshots
   *  (period configurable), null until a full window of snapshots exists or
   *  after a gap resets the window. */
  ma1: number | null;
  ma2: number | null;
  /** Set only at points where holding direction and price direction can be
   *  compared (both sides of a real snapshot pair, no gap between them) —
   *  drawn as a small marker at the bar's own value. */
  divMarker: number | null;
  divergence: ForeignHoldingDivergence | null;
}

// Deliberately NOT a custom draw()-based indicator. Every previous attempt at
// hand-painting this pane's positioning/scale (dual axes, native-axis
// suppression, visible-range fitting, centering) ran into the same class of
// bug in one form or another. Plain figures let klinecharts own 100% of the
// positioning, scaling and pane sizing itself — the same mechanism
// MACD/VOL/WEEK52_HLN already rely on reliably. The MA lines share the
// bars' own unit (shares), so they ride the same native scale with no
// dual-axis problem; divergence markers do too, since they're plotted at
// the bar's own value.
//
// Coloring is done via each figure's own `styles` callback (not the
// template-level static upColor/downColor/noChangeColor triplet) — that
// static mechanism turned out to not diff consecutive bar values for a
// plain indicator figure the way it does for candles, leaving every bar
// grey. The callback gives explicit prev/current indicatorData, so both the
// up/down/no-change comparison and the divergence-color lookup are fully
// under our control.
const foreignHolding: IndicatorTemplate<CalcResult> = {
  name: 'FOREIGN_HOLDING',
  shortName: 'FGN',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  precision: 0,
  // Share counts have no meaningful decimal places, and the native axis
  // shows large values much more readably as 20.9M/38.6K than the raw
  // number — this is klinecharts' own K/M/B formatting, not something we
  // paint ourselves.
  shouldFormatBigNumber: true,

  figures: [
    {
      key: 'absHolding',
      type: 'bar',
      styles: (data) => {
        const current = data.current.indicatorData?.absHolding;
        const prev = data.prev.indicatorData?.absHolding;
        const store = useChartStore.getState();
        let color = store.foreignHoldingFlatColor;
        if (current != null && prev != null) {
          if (current > prev) color = store.foreignHoldingUpColor;
          else if (current < prev) color = store.foreignHoldingDownColor;
        }
        return { style: PolygonType.Fill, color };
      },
    },
    {
      key: 'ma1',
      title: '',
      type: 'line',
      styles: () => ({ size: 1.3, color: useChartStore.getState().foreignHoldingMA1Color, smooth: false }),
    },
    {
      key: 'ma2',
      title: '',
      type: 'line',
      styles: () => ({ size: 1.3, color: useChartStore.getState().foreignHoldingMA2Color, smooth: false }),
    },
    {
      key: 'divMarker',
      title: '',
      type: 'circle',
      styles: (data) => {
        const div = data.current.indicatorData?.divergence;
        const store = useChartStore.getState();
        const colorMap: Record<ForeignHoldingDivergence, string> = {
          'confirm-bull': store.foreignHoldingConfirmBullColor,
          distribution: store.foreignHoldingDistributionColor,
          accumulation: store.foreignHoldingAccumulationColor,
          'confirm-bear': store.foreignHoldingConfirmBearColor,
        };
        return { style: PolygonType.Fill, color: div ? colorMap[div] : 'transparent' };
      },
    },
  ],

  calc: (dataList) => {
    const points = window.__foreign_holding_data?.points ?? [];
    const bars = dataList as unknown as KBar[];
    const results: CalcResult[] = bars.map(() => ({ absHolding: null, ma1: null, ma2: null, divMarker: null, divergence: null }));
    if (points.length === 0) return results;

    const store = useChartStore.getState();
    const period1 = Math.max(1, Math.round(store.foreignHoldingMA1Period));
    const period2 = Math.max(1, Math.round(store.foreignHoldingMA2Period));

    // Anchor each snapshot to its nearest bar (same day-tolerance guard as
    // before), in chronological order, so the MA windows and divergence
    // comparison walk the real snapshot sequence rather than the full
    // (mostly-null) per-bar array. Keeps a reference to the original point
    // object so the computed divergence can be written back onto it (below).
    const matched: { idx: number; time: number; foreignHolding: number; pt: ForeignHoldingChartPoint }[] = [];
    for (const pt of points) {
      const idx = findClosestBarIndex(bars, pt.time);
      if (idx < 0) continue;
      if (Math.abs(Math.floor(bars[idx].timestamp / 1000) - pt.time) > 86400) continue;
      matched.push({ idx, time: pt.time, foreignHolding: pt.foreignHolding, pt });
    }
    matched.sort((a, b) => a.idx - b.idx);

    // Marker sits a fixed fraction of the overall visible value range above
    // its bar, rather than exactly on top of it, so it reads as a separate
    // annotation instead of being swallowed by the bar itself.
    const allVals = matched.map((m) => m.foreignHolding);
    const valueSpan = allVals.length > 0 ? Math.max(...allVals) - Math.min(...allVals) : 0;
    const markerOffset = valueSpan > 0 ? valueSpan * 0.08 : (allVals[0] ?? 0) * 0.02;

    const window1: number[] = [];
    const window2: number[] = [];
    let prev: typeof matched[number] | null = null;

    for (const m of matched) {
      const gapBefore = prev != null && (m.time - prev.time) / 86400 > GAP_DAYS_THRESHOLD;
      if (gapBefore) { window1.length = 0; window2.length = 0; }

      window1.push(m.foreignHolding);
      if (window1.length > period1) window1.shift();
      window2.push(m.foreignHolding);
      if (window2.length > period2) window2.shift();

      const ma1 = window1.length === period1 ? window1.reduce((a, b) => a + b, 0) / period1 : null;
      const ma2 = window2.length === period2 ? window2.reduce((a, b) => a + b, 0) / period2 : null;

      let divMarker: number | null = null;
      let divergence: ForeignHoldingDivergence | null = null;
      if (prev && !gapBefore) {
        const holdDir = Math.sign(m.foreignHolding - prev.foreignHolding);
        const priceDir = Math.sign(bars[m.idx].close - bars[prev.idx].close);
        if (holdDir > 0 && priceDir > 0) divergence = 'confirm-bull';
        else if (holdDir < 0 && priceDir > 0) divergence = 'distribution';
        else if (holdDir > 0 && priceDir < 0) divergence = 'accumulation';
        else if (holdDir < 0 && priceDir < 0) divergence = 'confirm-bear';
        if (divergence) divMarker = m.foreignHolding + markerOffset;
      }
      m.pt.divergence = divergence;

      results[m.idx] = { absHolding: m.foreignHolding, ma1, ma2, divMarker, divergence };
      prev = m;
    }

    return results;
  },
};

export default foreignHolding;
